import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getPartners, createPartner, updatePartner, deletePartner,
  uploadPartnerLogo, uploadPartnerOnesheet, deletePartnerOnesheet, errMsg,
} from '../lib/api';
import { resolveMediaUrl } from '../lib/video';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Handshake, ExternalLink, Plus, Pencil, Trash2, Upload, FileText, Image as ImageIcon, Building2,
} from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = { name: '', description: '', website_url: '' };

export default function PartnerMarketplace() {
  const { isSuperAdmin } = useAuth();
  const isAdmin = isSuperAdmin();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchPartners = useCallback(async () => {
    try {
      const res = await getPartners();
      setPartners(res.data || []);
    } catch {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name || '', description: p.description || '', website_url: p.website_url || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Partner name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await updatePartner(editing.id, form);
        toast.success('Partner updated');
      } else {
        await createPartner(form);
        toast.success('Partner added');
      }
      setDialogOpen(false);
      fetchPartners();
    } catch (e) {
      toast.error(errMsg(e, 'Failed to save partner'));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deletePartner(deleteTarget.id);
      toast.success('Partner removed');
      setDeleteTarget(null);
      fetchPartners();
    } catch {
      toast.error('Failed to remove partner');
    }
  };

  const handleLogoUpload = async (partnerId, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      await uploadPartnerLogo(partnerId, f);
      toast.success('Logo updated');
      fetchPartners();
    } catch (err) {
      toast.error(errMsg(err, 'Logo upload failed'));
    }
    e.target.value = '';
  };

  const handleOnesheetUpload = async (partnerId, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      await uploadPartnerOnesheet(partnerId, f, '');
      toast.success('Onesheeter added');
      fetchPartners();
    } catch (err) {
      toast.error(errMsg(err, 'Onesheeter upload failed'));
    }
    e.target.value = '';
  };

  const handleOnesheetDelete = async (partnerId, sheetId) => {
    try {
      await deletePartnerOnesheet(partnerId, sheetId);
      toast.success('Onesheeter removed');
      fetchPartners();
    } catch {
      toast.error('Failed to remove onesheeter');
    }
  };

  return (
    <div className="space-y-6" data-testid="partner-marketplace-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center">
            <Handshake className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight leading-none">
              Partner Marketplace
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Vetted partners and member benefits, curated by Players Health.
            </p>
          </div>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} data-testid="add-partner-btn" className="gap-1.5">
            <Plus className="w-4 h-4" /> Add Partner
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading partners…</p>
      ) : partners.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">
          No partners yet.
        </Card>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {partners.map((p) => (
            <Card key={p.id} className="flex flex-col p-5 hover:shadow-lg transition-shadow"
              data-testid={`partner-card-${p.id}`}>
              {/* Logo */}
              <div className="h-20 flex items-center justify-start mb-4">
                {p.logo_url ? (
                  <img src={resolveMediaUrl(p.logo_url)} alt={p.name}
                    className="max-h-16 max-w-[160px] object-contain" />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-secondary flex items-center justify-center">
                    <Building2 className="w-7 h-7 text-muted-foreground" />
                  </div>
                )}
              </div>

              <h3 className="text-lg font-semibold leading-tight">{p.name}</h3>
              {p.description && (
                <p className="text-sm text-muted-foreground mt-1.5 flex-1">{p.description}</p>
              )}

              {/* Onesheeters */}
              {(p.onesheets?.length > 0 || isAdmin) && (
                <div className="mt-4 space-y-1.5">
                  {p.onesheets?.length > 0 && (
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Resources</p>
                  )}
                  {(p.onesheets || []).map((s) => (
                    <div key={s.id} className="flex items-center gap-2 text-sm">
                      <a href={resolveMediaUrl(s.url)} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-primary hover:underline flex-1 min-w-0"
                        data-testid={`onesheet-link-${s.id}`}>
                        <FileText className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{s.title}</span>
                      </a>
                      {isAdmin && (
                        <button onClick={() => handleOnesheetDelete(p.id, s.id)}
                          className="text-muted-foreground hover:text-destructive"
                          data-testid={`onesheet-delete-${s.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* CTA */}
              <a href={p.website_url || '#'} target="_blank" rel="noopener noreferrer"
                className="mt-5"
                data-testid={`partner-link-${p.id}`}>
                <Button className="w-full gap-1.5" disabled={!p.website_url}>
                  Visit Partner <ExternalLink className="w-4 h-4" />
                </Button>
              </a>

              {/* Admin controls */}
              {isAdmin && (
                <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-1.5">
                  <Button variant="outline" size="sm" onClick={() => openEdit(p)}
                    data-testid={`edit-partner-${p.id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <label className="inline-flex">
                    <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg,.gif" className="hidden"
                      onChange={(e) => handleLogoUpload(p.id, e)} data-testid={`logo-input-${p.id}`} />
                    <span className="inline-flex items-center text-xs px-2.5 py-1.5 rounded-md border border-border cursor-pointer hover:bg-secondary">
                      <ImageIcon className="w-3.5 h-3.5 mr-1" /> Logo
                    </span>
                  </label>
                  <label className="inline-flex">
                    <input type="file" accept=".pdf" className="hidden"
                      onChange={(e) => handleOnesheetUpload(p.id, e)} data-testid={`onesheet-input-${p.id}`} />
                    <span className="inline-flex items-center text-xs px-2.5 py-1.5 rounded-md border border-border cursor-pointer hover:bg-secondary">
                      <Upload className="w-3.5 h-3.5 mr-1" /> Onesheeter
                    </span>
                  </label>
                  <Button variant="outline" size="sm" onClick={() => setDeleteTarget(p)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`delete-partner-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Partner' : 'Add Partner'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="p-name">Name *</Label>
              <Input id="p-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Partner name" data-testid="partner-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-url">Referral / Website URL</Label>
              <Input id="p-url" value={form.website_url}
                onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                placeholder="https://partner.com/players-health" data-testid="partner-url-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-desc">Description</Label>
              <Textarea id="p-desc" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short description of the partner offering" rows={3}
                data-testid="partner-desc-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-partner-btn">
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Add partner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove partner?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove {`"${deleteTarget?.name}"`} and its uploaded resources.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-partner-btn">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
